import { Modal, Button, Input, Select, InputNumber, message, Spin, Checkbox } from "antd";
import { Package, FileText, AlertCircle, Check, Truck, Box, ChevronDown, ChevronRight } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { TServiceTicket } from "~/types";
import { InventoryStatusCode, InventoryType, PackingTypes, JobStatus, ServiceType } from "~/utils/constants";
import { useCreateInventory } from "~/services/inventory/inventory.mutations";
import { inventoryTemplatesApi } from "~/services/inventory-templates/inventory-templates.api";
import { InventoryTemplate } from "~/services/inventory-templates/inventory-templates.types";
import { useQueryClient } from "@tanstack/react-query";
import { BOOKING_KEYS } from "~/services/booking";
import { capitalize } from "~/utils";

interface InventoryRowData {
  key: string;
  templateId: string;
  templateName: string;
  sourceTicket: string;
  sourceTicketId: string;
  serviceType: ServiceType;
  // From template
  description?: string;
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  packingType?: PackingTypes;
  medium?: string;
  // User input
  title: string;
  artistName: string;
  itemType: InventoryType;
  value?: number;
  clientInternalRef?: string;
  // Status
  isSelected: boolean;
  isCreated: boolean;
}

interface ServiceGroup {
  ticketId: string;
  ticketSerial: string;
  serviceType: ServiceType;
  rows: InventoryRowData[];
  isExpanded: boolean;
}

interface ConvertTemplatesModalProps {
  open: boolean;
  onClose: () => void;
  serviceTickets: TServiceTicket[];
  clientId: string;
  bookingId: string;
}

const packingTypeOptions = [
  { label: "Loose", value: PackingTypes.LOOSE },
  { label: "Slipcase", value: PackingTypes.SLIPCASE },
  { label: "CB Fold", value: PackingTypes.CB_FOLD },
  { label: "Shadow Box", value: PackingTypes.SHADOW_BOX },
  { label: "Crate", value: PackingTypes.CRATE },
  { label: "Slat Crate", value: PackingTypes.SLATCRATE },
  { label: "Box", value: PackingTypes.BOX },
  { label: "Pallet", value: PackingTypes.PALLET },
  { label: "C-Bin", value: PackingTypes.CBIN },
  { label: "T-Frame", value: PackingTypes.T_Frame },
  { label: "Blanket Wrapped", value: PackingTypes.BLANKET_WRAPPED },
  { label: "Poly Only", value: PackingTypes.POLY_ONLY },
  { label: "Poly/Foam", value: PackingTypes.POLY_FOAM },
  { label: "Custom", value: PackingTypes.CUSTOM },
];

const itemTypeOptions = [
  { label: "Piece", value: InventoryType.PIECE },
  { label: "Package", value: InventoryType.PACKAGE },
];

const getServiceIcon = (serviceType: ServiceType) => {
  switch (serviceType) {
    case ServiceType.COLLECTION:
    case ServiceType.DELIVERY:
      return <Truck size={18} className="text-blue-600" />;
    default:
      return <Box size={18} className="text-blue-600" />;
  }
};

export const ConvertTemplatesModal: React.FC<ConvertTemplatesModalProps> = ({
  open,
  onClose,
  serviceTickets,
  clientId,
  bookingId,
}) => {
  const [inventoryRows, setInventoryRows] = useState<InventoryRowData[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [templateCache, setTemplateCache] = useState<Record<string, InventoryTemplate>>({});
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const { mutateAsync: createInventory } = useCreateInventory();
  const queryClient = useQueryClient();

  // Get estimated service tickets
  const estimatedTickets = useMemo(() => {
    return serviceTickets.filter(
      ticket => ticket.status === JobStatus.ESTIMATED && ticket.estimateTemplates?.length
    );
  }, [serviceTickets]);

  // Fetch template details when modal opens
  useEffect(() => {
    if (open && estimatedTickets.length > 0) {
      fetchTemplateDetails();
      // Expand all groups by default
      setExpandedGroups(new Set(estimatedTickets.map(t => t.id)));
    }
  }, [open, estimatedTickets]);

  const fetchTemplateDetails = async () => {
    setLoading(true);
    try {
      // Collect all template IDs
      const allTemplateIds: string[] = [];
      estimatedTickets.forEach(ticket => {
        ticket.estimateTemplates?.forEach((template: any) => {
          if (!allTemplateIds.includes(template.templateId)) {
            allTemplateIds.push(template.templateId);
          }
        });
      });

      // Fetch templates that aren't cached
      const templatesNeeded = allTemplateIds.filter(id => !templateCache[id]);

      const newCache = { ...templateCache };
      for (const templateId of templatesNeeded) {
        try {
          const template = await inventoryTemplatesApi.getById(templateId);
          newCache[templateId] = template;
        } catch (err) {
          console.error(`Failed to fetch template ${templateId}:`, err);
        }
      }
      setTemplateCache(newCache);

      // Build inventory rows from estimate templates
      const rows: InventoryRowData[] = [];
      estimatedTickets.forEach(ticket => {
        ticket.estimateTemplates?.forEach((template: any, templateIndex: number) => {
          // Expand quantity into individual rows
          for (let i = 0; i < template.quantity; i++) {
            const templateData = newCache[template.templateId];
            rows.push({
              key: `${template.templateId}-${ticket.id}-${templateIndex}-${i}`,
              templateId: template.templateId,
              templateName: template.templateName,
              sourceTicket: ticket.serial,
              sourceTicketId: ticket.id,
              serviceType: ticket.serviceType,
              // From template
              description: templateData?.description,
              length: templateData?.length || 0,
              width: templateData?.width || 0,
              height: templateData?.height || 0,
              weight: templateData?.weight || 0,
              packingType: (template.packingType as PackingTypes) || templateData?.packingType,
              medium: templateData?.medium,
              // User input defaults
              title: template.templateName,
              artistName: "",
              itemType: InventoryType.PIECE,
              value: undefined,
              clientInternalRef: "",
              // Status
              isSelected: true,
              isCreated: false,
            });
          }
        });
      });

      setInventoryRows(rows);
    } catch (err) {
      console.error("Failed to fetch template details:", err);
      message.error("Failed to load template details");
    } finally {
      setLoading(false);
    }
  };

  // Group rows by service ticket
  const serviceGroups = useMemo((): ServiceGroup[] => {
    const groupMap = new Map<string, ServiceGroup>();

    inventoryRows.forEach(row => {
      if (!groupMap.has(row.sourceTicketId)) {
        groupMap.set(row.sourceTicketId, {
          ticketId: row.sourceTicketId,
          ticketSerial: row.sourceTicket,
          serviceType: row.serviceType,
          rows: [],
          isExpanded: expandedGroups.has(row.sourceTicketId),
        });
      }
      groupMap.get(row.sourceTicketId)!.rows.push(row);
    });

    return Array.from(groupMap.values());
  }, [inventoryRows, expandedGroups]);

  const updateRow = (key: string, field: keyof InventoryRowData, value: any) => {
    setInventoryRows(rows =>
      rows.map(row =>
        row.key === key ? { ...row, [field]: value } : row
      )
    );
  };

  const toggleRowSelection = (key: string) => {
    setInventoryRows(rows =>
      rows.map(row =>
        row.key === key ? { ...row, isSelected: !row.isSelected } : row
      )
    );
  };

  const toggleGroupExpanded = (ticketId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ticketId)) {
        newSet.delete(ticketId);
      } else {
        newSet.add(ticketId);
      }
      return newSet;
    });
  };

  const toggleGroupSelection = (ticketId: string) => {
    const groupRows = inventoryRows.filter(r => r.sourceTicketId === ticketId && !r.isCreated);
    const allSelected = groupRows.every(r => r.isSelected);

    setInventoryRows(rows =>
      rows.map(row =>
        row.sourceTicketId === ticketId && !row.isCreated
          ? { ...row, isSelected: !allSelected }
          : row
      )
    );
  };

  const selectAll = () => {
    setInventoryRows(rows =>
      rows.map(row => ({ ...row, isSelected: !row.isCreated }))
    );
  };

  const deselectAll = () => {
    setInventoryRows(rows =>
      rows.map(row => ({ ...row, isSelected: false }))
    );
  };

  const handleCreateInventory = async () => {
    const selectedRows = inventoryRows.filter(row => row.isSelected && !row.isCreated);

    if (selectedRows.length === 0) {
      message.warning("Please select at least one item to create");
      return;
    }

    // Validate required fields
    const invalidRows = selectedRows.filter(row => !row.title || !row.artistName);
    if (invalidRows.length > 0) {
      message.error("Please fill in Title and Artist Name for all selected items");
      return;
    }

    setCreating(true);
    let successCount = 0;
    let failCount = 0;

    for (const row of selectedRows) {
      try {
        const payload: any = {
          title: row.title,
          artistName: row.artistName,
          clientId: clientId,
          length: row.length || 0,
          width: row.width || 0,
          height: row.height || 0,
          weight: row.weight || 0,
          status: InventoryStatusCode.NO_POSSESSION,
          itemType: row.itemType,
          serviceTicketId: row.sourceTicketId,
        };

        if (row.description) payload.description = row.description;
        if (row.packingType) payload.packingType = row.packingType;
        if (row.medium) payload.medium = row.medium;
        if (row.value) payload.value = row.value;
        if (row.clientInternalRef) payload.clientInternalRef = row.clientInternalRef;

        await createInventory(payload);

        setInventoryRows(rows =>
          rows.map(r =>
            r.key === row.key ? { ...r, isCreated: true, isSelected: false } : r
          )
        );
        successCount++;
      } catch (err) {
        console.error(`Failed to create inventory for ${row.title}:`, err);
        failCount++;
      }
    }

    queryClient.invalidateQueries({ queryKey: BOOKING_KEYS.GET_BOOKING(bookingId) });

    if (successCount > 0) {
      message.success(`Successfully created ${successCount} inventory item${successCount > 1 ? 's' : ''}`);
    }
    if (failCount > 0) {
      message.error(`Failed to create ${failCount} item${failCount > 1 ? 's' : ''}`);
    }

    setCreating(false);
  };

  const selectedCount = inventoryRows.filter(r => r.isSelected && !r.isCreated).length;
  const createdCount = inventoryRows.filter(r => r.isCreated).length;
  const totalCount = inventoryRows.length;

  const handleClose = () => {
    setInventoryRows([]);
    setExpandedGroups(new Set());
    onClose();
  };

  const renderRow = (row: InventoryRowData) => (
    <div
      key={row.key}
      className={`grid grid-cols-12 gap-2 p-2 border-b items-center text-sm ${
        row.isCreated ? "bg-green-50" : row.isSelected ? "bg-blue-50" : "bg-white"
      }`}
    >
      {/* Checkbox */}
      <div className="col-span-1 flex justify-center">
        <Checkbox
          checked={row.isSelected}
          disabled={row.isCreated}
          onChange={() => toggleRowSelection(row.key)}
        />
      </div>

      {/* Template Name */}
      <div className="col-span-1">
        <div className="font-medium text-xs truncate" title={row.templateName}>
          {row.templateName}
        </div>
      </div>

      {/* Title */}
      <div className="col-span-2">
        <Input
          value={row.title}
          onChange={(e) => updateRow(row.key, "title", e.target.value)}
          disabled={row.isCreated}
          size="small"
          placeholder="Title *"
        />
      </div>

      {/* Artist */}
      <div className="col-span-1">
        <Input
          value={row.artistName}
          onChange={(e) => updateRow(row.key, "artistName", e.target.value)}
          disabled={row.isCreated}
          size="small"
          placeholder="Artist *"
        />
      </div>

      {/* Client Ref */}
      <div className="col-span-1">
        <Input
          value={row.clientInternalRef}
          onChange={(e) => updateRow(row.key, "clientInternalRef", e.target.value)}
          disabled={row.isCreated}
          size="small"
          placeholder="Ref #"
        />
      </div>

      {/* Item Type */}
      <div className="col-span-1">
        <Select
          value={row.itemType}
          onChange={(v) => updateRow(row.key, "itemType", v)}
          disabled={row.isCreated}
          size="small"
          style={{ width: "100%" }}
          options={itemTypeOptions}
        />
      </div>

      {/* Dimensions */}
      <div className="col-span-2">
        <div className="flex items-center space-x-1">
          <InputNumber
            value={row.length}
            onChange={(v) => updateRow(row.key, "length", v)}
            disabled={row.isCreated}
            size="small"
            style={{ width: 45 }}
            min={0}
          />
          <span className="text-gray-400">×</span>
          <InputNumber
            value={row.width}
            onChange={(v) => updateRow(row.key, "width", v)}
            disabled={row.isCreated}
            size="small"
            style={{ width: 45 }}
            min={0}
          />
          <span className="text-gray-400">×</span>
          <InputNumber
            value={row.height}
            onChange={(v) => updateRow(row.key, "height", v)}
            disabled={row.isCreated}
            size="small"
            style={{ width: 45 }}
            min={0}
          />
        </div>
      </div>

      {/* Packing */}
      <div className="col-span-1">
        <Select
          value={row.packingType}
          onChange={(v) => updateRow(row.key, "packingType", v)}
          disabled={row.isCreated}
          size="small"
          style={{ width: "100%" }}
          options={packingTypeOptions}
          allowClear
          placeholder="Packing"
        />
      </div>

      {/* Value */}
      <div className="col-span-1">
        <InputNumber
          value={row.value}
          onChange={(v) => updateRow(row.key, "value", v)}
          disabled={row.isCreated}
          size="small"
          style={{ width: "100%" }}
          min={0}
          prefix="$"
        />
      </div>

      {/* Status */}
      <div className="col-span-1 text-center">
        {row.isCreated ? (
          <span className="flex items-center justify-center text-green-600 text-xs">
            <Check size={12} className="mr-1" />
            Created
          </span>
        ) : (
          <span className="text-gray-400 text-xs">Pending</span>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <FileText className="text-blue-600" size={20} />
          <span>Convert Estimate Templates to Inventory</span>
        </div>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={1200}
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spin size="large" />
          <span className="ml-3">Loading templates...</span>
        </div>
      ) : inventoryRows.length === 0 ? (
        <div className="py-12 text-center">
          <Package size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No estimate templates found.</p>
          <p className="text-sm text-gray-400 mt-1">
            Templates are only available from service tickets with "Estimated" status.
          </p>
        </div>
      ) : (
        <>
          {/* Info Banner */}
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-2">
              <AlertCircle className="text-blue-600 mt-0.5" size={16} />
              <div>
                <p className="text-sm text-blue-800 font-medium">
                  Convert {totalCount} estimate template{totalCount > 1 ? 's' : ''} to inventory items
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Fill in the required fields (Title, Artist) and adjust other details as needed.
                </p>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm">
                <strong>{selectedCount}</strong> selected
              </span>
              {createdCount > 0 && (
                <span className="text-sm text-green-600">
                  <strong>{createdCount}</strong> created
                </span>
              )}
            </div>
            <div className="space-x-2">
              <Button size="small" onClick={selectAll}>Select All</Button>
              <Button size="small" onClick={deselectAll}>Deselect All</Button>
            </div>
          </div>

          {/* Column Headers */}
          <div className="grid grid-cols-12 gap-2 p-2 bg-gray-100 text-xs font-medium text-gray-600 rounded-t border">
            <div className="col-span-1 text-center">Select</div>
            <div className="col-span-1">Template</div>
            <div className="col-span-2">Title *</div>
            <div className="col-span-1">Artist *</div>
            <div className="col-span-1">Client Ref</div>
            <div className="col-span-1">Type</div>
            <div className="col-span-2">Dimensions (L×W×H)</div>
            <div className="col-span-1">Packing</div>
            <div className="col-span-1">Value</div>
            <div className="col-span-1 text-center">Status</div>
          </div>

          {/* Grouped Content */}
          <div className="border border-t-0 rounded-b max-h-[400px] overflow-y-auto">
            {serviceGroups.map((group) => {
              const groupSelectedCount = group.rows.filter(r => r.isSelected && !r.isCreated).length;
              const groupCreatedCount = group.rows.filter(r => r.isCreated).length;
              const groupTotalCount = group.rows.length;
              const allSelected = groupSelectedCount === groupTotalCount - groupCreatedCount && groupTotalCount > groupCreatedCount;
              const isExpanded = expandedGroups.has(group.ticketId);

              return (
                <div key={group.ticketId}>
                  {/* Service Group Header */}
                  <div
                    className="flex items-center justify-between p-3 bg-gray-50 border-b cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleGroupExpanded(group.ticketId)}
                  >
                    <div className="flex items-center space-x-3">
                      {isExpanded ? (
                        <ChevronDown size={16} className="text-gray-500" />
                      ) : (
                        <ChevronRight size={16} className="text-gray-500" />
                      )}
                      <Checkbox
                        checked={allSelected}
                        indeterminate={groupSelectedCount > 0 && !allSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleGroupSelection(group.ticketId);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {getServiceIcon(group.serviceType)}
                      <div>
                        <span className="font-medium">{group.ticketSerial}</span>
                        <span className="mx-2 text-gray-400">•</span>
                        <span className="text-gray-600">{capitalize(group.serviceType)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 text-sm">
                      <span className="text-gray-500">
                        {groupTotalCount} item{groupTotalCount !== 1 ? 's' : ''}
                      </span>
                      {groupCreatedCount > 0 && (
                        <span className="text-green-600">
                          {groupCreatedCount} created
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Group Rows */}
                  {isExpanded && group.rows.map(row => renderRow(row))}
                </div>
              );
            })}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end space-x-3 border-t pt-4">
            <Button onClick={handleClose}>Close</Button>
            <Button
              type="primary"
              onClick={handleCreateInventory}
              loading={creating}
              disabled={selectedCount === 0}
              className="bg-blue-600 hover:bg-blue-700"
              icon={<Package size={14} />}
            >
              Create {selectedCount} Inventory Item{selectedCount !== 1 ? 's' : ''}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};
